/**
 * Recovers a tab left holding a stale index.html after a deploy.
 *
 * <p>Every route in this app is `lazy(() => import(...))` behind a Suspense fallback — 26 of them.
 * A deploy emits new content-hashed chunk filenames and the container swap removes the old ones, so
 * a tab that loaded the previous index.html still points at chunk URLs that now 404. The dynamic
 * import rejects, Suspense never resolves, and the fallback renders forever: "Loading…" on every
 * route the user visits, with no error and nothing to click. The supplier detail route says
 * "Loading supplier..." — which reads as a page that has no edit controls rather than a page that
 * never mounted.
 *
 * <p>One reload fixes it, because it fetches the current index.html and therefore the current chunk
 * names. Vite fires `vite:preloadError` on the window when a dynamic import fails to preload, which
 * is the hook this listens to.
 */

/** Long enough that a genuinely broken deploy surfaces as an error instead of a reload loop. */
export const RELOAD_COOLDOWN_MS = 15_000;

const RELOAD_MARK_KEY = "taxos:stale-chunk-reload-at";

/**
 * Whether a chunk failure right now should trigger a reload.
 *
 * <p>The cooldown is the whole safety argument: if the reload does not fix it — the asset really is
 * gone, the user is offline, the deploy is broken — the second failure must NOT reload again, or the
 * tab spins forever in a refresh loop that also destroys any unsaved input. One attempt, then let
 * the error surface.
 */
export function shouldReloadAfterChunkFailure(now: number, lastReloadAt: number | null): boolean {
	if (lastReloadAt === null) {
		return true;
	}
	// A clock that moved backwards (system time change, restored tab) must not read as "long ago".
	const elapsed = now - lastReloadAt;
	return elapsed < 0 || elapsed > RELOAD_COOLDOWN_MS;
}

function readMark(): number | null {
	try {
		const raw = window.sessionStorage.getItem(RELOAD_MARK_KEY);
		if (raw === null) {
			return null;
		}
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	} catch {
		// Private mode / storage disabled: treat as "never reloaded". Worst case is one extra reload,
		// which is the behaviour we want anyway; refusing to recover would be worse.
		return null;
	}
}

function writeMark(at: number): void {
	try {
		window.sessionStorage.setItem(RELOAD_MARK_KEY, String(at));
	} catch {
		// Ignored for the same reason as above — this is a guard, not a requirement.
	}
}

/** Installs the listener. Returns a teardown so tests and hot reloads do not stack handlers. */
export function installStaleChunkRecovery(): () => void {
	const onPreloadError = (event: Event) => {
		if (!shouldReloadAfterChunkFailure(Date.now(), readMark())) {
			return; // Already tried; let Vite throw so the failure is visible rather than silent.
		}
		// Stops Vite rethrowing: we are handling it by reloading.
		event.preventDefault();
		writeMark(Date.now());
		window.location.reload();
	};
	window.addEventListener("vite:preloadError", onPreloadError);
	return () => window.removeEventListener("vite:preloadError", onPreloadError);
}
